import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const ProfilePage = () => {
  const { getUserProfile, updateUserProfile } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
  });
  const [passwords, setPasswords] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  /* eslint-disable no-unused-vars */
  const [error, setError] = useState(null);
  /* eslint-enable no-unused-vars */
  const [success, setSuccess] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const userData = await getUserProfile();
        setProfile({
          name: userData.name,
          email: userData.email,
        });
      } catch (err) {
        setError('Failed to load profile data');
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // Empty dependency array to run only once

  // Handle profile form change
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle password form change
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear password error when typing
    if (passwordError) {
      setPasswordError(null);
    }
  };

  // Save profile changes
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      await updateUserProfile({
        name: profile.name,
        email: profile.email
      });
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Change password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (passwords.password !== passwords.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    // Validate password length
    if (passwords.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    setSaving(true);
    setPasswordError(null);
    
    try {
      await updateUserProfile({ password: passwords.password });
      setSuccess('Password changed successfully');
      
      // Clear password fields
      setPasswords({
        password: '',
        confirmPassword: ''
      });
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  // Close success notification
  const handleCloseSuccess = () => {
    setSuccess(null);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
        User Profile
      </Typography>

      {/* Profile Information */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          border: '1px solid #eee', 
          borderRadius: 2 
        }}
      >
        <Typography variant="h5" gutterBottom>
          Profile Information
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handleProfileSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={profile.name}
                onChange={handleProfileChange}
                variant="outlined"
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={profile.email}
                onChange={handleProfileChange}
                variant="outlined"
                required
                type="email"
                margin="normal"
              />
            </Grid>
          </Grid>

          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            disabled={saving}
            sx={{ 
              mt: 3,
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </Box>
      </Paper>

      {/* Change Password */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          border: '1px solid #eee', 
          borderRadius: 2 
        }}
      >
        <Typography variant="h5" gutterBottom>
          Change Password
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box component="form" onSubmit={handlePasswordSubmit} noValidate>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="New Password"
                name="password"
                value={passwords.password}
                onChange={handlePasswordChange}
                variant="outlined"
                required
                type="password"
                margin="normal"
                error={!!passwordError}
                helperText={passwordError && passwordError.includes('Password must be') ? passwordError : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                variant="outlined"
                required
                type="password"
                margin="normal"
                error={!!passwordError}
                helperText={passwordError && passwordError.includes('do not match') ? passwordError : ''}
              />
            </Grid>
          </Grid>

          {passwordError && !passwordError.includes('do not match') && !passwordError.includes('Password must be') && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {passwordError}
            </Alert>
          )}

          <Button 
            type="submit" 
            variant="contained"
            disabled={saving} 
            sx={{ 
              mt: 3,
              backgroundColor: 'black',
              '&:hover': {
                backgroundColor: '#333',
              }
            }}
          >
            {saving ? <CircularProgress size={24} /> : 'Change Password'}
          </Button>
        </Box>
      </Paper>

      {/* Success notification */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProfilePage;